import type { ReactNode } from 'react'

/**
 * Les icônes du contenu — 06 §5.
 *
 * **Aucun emoji dans l'interface.** Un emoji n'est pas une icône : c'est un
 * caractère, rendu par une police que nous ne choisissons pas. Il change de
 * dessin, de couleur et de taille d'un système à l'autre — un cadenas gris
 * chez l'un, jaune vif chez l'autre — il ne suit ni la couleur du texte ni le
 * thème sombre, il se sélectionne, et un lecteur d'écran l'annonce par son nom
 * Unicode au milieu d'une phrase. À l'écran, il jure avec tout le reste.
 *
 * Ces icônes reprennent EXACTEMENT la facture du rail de navigation
 * (`components/shell/navigation.tsx`) : viewBox de 24, `currentColor`, un seul
 * tracé quand c'est possible. La navigation garde les siennes — elles portent
 * un état plein/creux dont le contenu n'a pas besoin — mais les deux jeux se
 * ressemblent parce qu'ils sont dessinés sur la même grille.
 *
 * **Toujours décoratives par défaut.** `aria-hidden` est posé ici : une coche
 * à côté du mot « terminé » ne doit pas être annoncée deux fois. Quand l'icône
 * porte seule le sens, on passe un `titre` — et elle devient une image nommée.
 */
export type NomDIcone =
  | 'coche'
  | 'cadenas'
  | 'recherche'
  | 'fleche-gauche'
  | 'fleche-droite'
  | 'croix'
  | 'horloge'
  | 'document'
  | 'nuage'
  | 'point'
  | 'marque-page'
  | 'marque-page-plein'
  | 'partager'
  | 'explication'
  | 'lecon'
  | 'trois-points'
  | 'drapeau'
  | 'chevron-bas'
  | 'chevron-droite'
  | 'oeil'
  | 'personnes'
  | 'reponses'
  | 'cible'
  | 'filtres'
  | 'rafraichir'
  | 'grouper-matiere'
  | 'grouper-annee'
  | 'calendrier'
  | 'lien'
  | 'whatsapp'
  | 'image'
  | 'alerte'

const TRACES: Record<NomDIcone, ReactNode> = {
  coche: <path d="M9.55 17.6 4 12.05l1.4-1.4 4.15 4.15 9.05-9.05 1.4 1.4z" />,
  cadenas: (
    <path d="M12 2a4.5 4.5 0 0 0-4.5 4.5V9H6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10a1 1 0 0 0-1-1h-1.5V6.5A4.5 4.5 0 0 0 12 2zm0 2a2.5 2.5 0 0 1 2.5 2.5V9h-5V6.5A2.5 2.5 0 0 1 12 4zm0 10.5a1.5 1.5 0 0 1 .75 2.8V19h-1.5v-1.7a1.5 1.5 0 0 1 .75-2.8z" />
  ),
  recherche: (
    <path d="M10.25 3.75a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0-1.75a8.25 8.25 0 1 0 5.02 14.79l4.47 4.47 1.5-1.5-4.47-4.47A8.25 8.25 0 0 0 10.25 2z" />
  ),
  'fleche-gauche': <path d="M10.8 19.3 3.5 12l7.3-7.3 1.4 1.4L7.3 11H20.5v2H7.3l4.9 4.9z" />,
  'fleche-droite': <path d="M13.2 4.7 20.5 12l-7.3 7.3-1.4-1.4 4.9-4.9H3.5v-2h13.2l-4.9-4.9z" />,
  croix: <path d="m12 10.6 5.3-5.3 1.4 1.4-5.3 5.3 5.3 5.3-1.4 1.4-5.3-5.3-5.3 5.3-1.4-1.4 5.3-5.3-5.3-5.3 1.4-1.4z" />,
  horloge: (
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm-1 3v6l4.9 2.9 1-1.7-3.9-2.3V7z" />
  ),
  document: (
    <path d="M6 2h8l4 4v16H6zm7.5 1.5V7H17zM8 11h8v1.5H8zm0 3.5h8V16H8zm0 3.5h5v1.5H8z" />
  ),
  nuage: (
    <path d="M6.5 19A4.5 4.5 0 0 1 5.9 10a6 6 0 0 1 11.7 1.2A3.9 3.9 0 0 1 17 19z" />
  ),
  point: <circle cx="12" cy="12" r="4" />,
  'marque-page': (
    <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4.2L5 21V4a1 1 0 0 1 1-1zm1 2v12.5l5-3 5 3V5z" />
  ),
  'marque-page-plein': <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4.2L5 21V4a1 1 0 0 1 1-1z" />,
  // Une flèche qui SORT d'une boîte — la grammaire universelle de l'envoi.
  // L'ancien tracé se lisait comme un téléversement de fichier.
  partager: (
    <path d="M11 3.4 7.7 6.7 6.3 5.3 12 -.4l5.7 5.7-1.4 1.4L13 3.4V15h-2zM4 13v6h16v-6h2v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-7z" />
  ),
  explication: (
    <path d="M12 2a7 7 0 0 0-4 12.7V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.3A7 7 0 0 0 12 2zM9.5 20h5v1.5h-5z" />
  ),
  lecon: (
    <path d="M4 3.5h6a3 3 0 0 1 2 .77A3 3 0 0 1 14 3.5h6a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-6a1.5 1.5 0 0 0-1.06.44L12 20.88l-.94-.94A1.5 1.5 0 0 0 10 19.5H4a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1zm1 2v12h5c.35 0 .69.06 1 .17V6.5a1 1 0 0 0-1-1H5z" />
  ),
  'trois-points': (
    <path d="M12 4.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 5.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 5.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
  ),
  drapeau: <path d="M5 2h1.5v20H5zm3 1.2h11l-2.3 4.3L19 12H8z" />,
  'chevron-bas': <path d="m12 15.4-6-6L7.4 8l4.6 4.6L16.6 8 18 9.4z" />,
  // **Le même chevron, tourné d'un quart de tour** — [DECISIONS §169]. Il dit
  // « il y a un étage derrière cette ligne » à l'entrée d'un palier. Le
  // dessiner plutôt que de faire pivoter le précédent en CSS : une rotation
  // s'applique aussi au trait, et le tracé n'a plus la même épaisseur apparente
  // que ses voisins sur la même colonne.
  'chevron-droite': <path d="m15.4 12-6 6L8 16.6l4.6-4.6L8 7.4 9.4 6z" />,
  oeil: (
    <path d="M12 5c-5 0-9 4.5-9 7s4 7 9 7 9-4.5 9-7-4-7-9-7zm0 2c3.6 0 6.6 3.1 7 5-.4 1.9-3.4 5-7 5s-6.6-3.1-7-5c.4-1.9 3.4-5 7-5zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
  ),
  // Une bulle de réponse — le compte des tentatives.
  reponses: (
    <path d="M4 3h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9.4L4.8 20.7A.5.5 0 0 1 4 20.3V17a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v10h1v2.6L8.6 15H19V5z" />
  ),
  // Une cible — le taux de réussite.
  cible: (
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z" />
  ),
  // Une flèche circulaire — la grammaire universelle du rafraîchissement.
  // Ouverte en haut à droite, avec sa pointe : un anneau fermé se lirait
  // comme un chargement, pas comme un geste.
  rafraichir: (
    <path d="M12 4a8 8 0 1 0 7.6 5.5l-1.9.6A6 6 0 1 1 12 6v3l4.5-4L12 1z" />
  ),
  // **Deux vues, deux icônes** — comme l'explorateur de fichiers, §105. La
  // bascule ne dit pas « change de mode » : chacune MONTRE la disposition
  // qu'elle donnera. Des tuiles pour le regroupement par matière, une liste
  // pour le regroupement par année.
  'grouper-matiere': (
    <path d="M4.6 4.6h5.6v5.6H4.6zM13.8 4.6h5.6v5.6h-5.6zM4.6 13.8h5.6v5.6H4.6zM13.8 13.8h5.6v5.6h-5.6z" />
  ),
  'grouper-annee': (
    <path d="M3.6 5.4h2.6v2.6H3.6zM8.8 5.6h11.6v2.2H8.8zM3.6 10.7h2.6v2.6H3.6zM8.8 10.9h11.6v2.2H8.8zM3.6 16h2.6v2.6H3.6zM8.8 16.2h11.6v2.2H8.8z" />
  ),
  // Un entonnoir : la grammaire des filtres. Les trois barres du réglage
  // auraient dit « préférences », ce qui n'est pas le geste ici.
  filtres: (
    <path d="M3.4 4.6A1 1 0 0 1 4.3 4h15.4a1 1 0 0 1 .78 1.63L14.5 13v5.2a1 1 0 0 1-.55.9l-3 1.5A1 1 0 0 1 9.5 19.7V13L3.52 5.63a1 1 0 0 1-.12-1.03z" />
  ),
  personnes: (
    <path d="M9 3a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7zm7.5 2a2.75 2.75 0 1 1 0 5.5 2.75 2.75 0 0 1 0-5.5zM9 11.5c3.3 0 6 1.7 6 4V21H3v-5.5c0-2.3 2.7-4 6-4zm7.5 1c2.5 0 4.5 1.3 4.5 3.1V21h-4.4v-5.5c0-1.1-.4-2.1-1.1-2.9z" />
  ),
  // Le repli plein du calendrier : `ARRONDIES` le sert, mais `TRACES` est un
  // enregistrement COMPLET — c'est ce qui garantit qu'aucun nom déclaré ne
  // rende un trou à l'écran.
  calendrier: (
    <path d="M7 2h2v2h6V2h2v2h2a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h2zm13 8H4v9h16z" />
  ),
  // Deux maillons de chaîne — [DECISIONS §192]. Le geste est « obtenir une
  // adresse », pas « envoyer » : l'icône de partage dit déjà l'envoi à un ami,
  // et réutiliser la même aurait mis deux actions différentes sous le même
  // signe, dans la même carte.
  lien: (
    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7a5 5 0 0 0 0 10h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8zm9-6h-4v1.9h4a3.1 3.1 0 0 1 0 6.2h-4V17h4a5 5 0 0 0 0-10z" />
  ),
  // Le repli plein de la chaîne WhatsApp, pour la même raison que le
  // calendrier juste au-dessus : `ARRONDIES` la sert, mais `TRACES` est un
  // enregistrement COMPLET, et c'est lui qui garantit qu'aucun nom déclaré ne
  // rende un trou à l'écran. TypeScript l'a d'ailleurs exigé avant que
  // quiconque ait pu l'oublier.
  whatsapp: (
    <path d="M12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.34A10 10 0 1 0 12 2zm0 2a8 8 0 1 1-4.13 14.85l-.35-.21-2.4.63.64-2.35-.22-.36A8 8 0 0 1 12 4zm-2.6 4.9c-.8 1.4-.2 3.5 1.6 5.2s3.8 2.4 5.2 1.6a1 1 0 0 0 .3-1.5l-1-1a1 1 0 0 0-1.3-.1l-.5.4a5.5 5.5 0 0 1-2.1-2.1l.4-.5a1 1 0 0 0-.1-1.3l-1-1a1 1 0 0 0-1.5.3z" />
  ),
  // Le repli plein de l'image — même raison que les trois précédentes :
  // `ARRONDIES` la sert, mais `TRACES` est un enregistrement COMPLET.
  image: (
    <path d="M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 13.2V18h14v-1.3l-4.2-4.2-2.9 2.9-3.1-3.1zM8.6 6.9a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4z" />
  ),
  // Idem : `ARRONDIES` la sert. Le triangle d'alerte porte le REFUS d'une
  // action (RG-D2 — jamais la couleur seule), et il ne se confond pas avec la
  // croix qui, à côté de lui, ferme le message.
  alerte: (
    <path d="M12 2.4 1.3 21h21.4L12 2.4zm0 4 7.2 12.6H4.8L12 6.4zm-1 3.6h2v5h-2zm0 6.4h2v2h-2z" />
  ),
}

/**
 * Les icônes ARRONDIES — RG-2.42 précisée, [DECISIONS §97].
 *
 * **Le même idiome que la navigation.** Celles-ci étaient des chemins pleins
 * dessinés d'angles vifs, à des épaisseurs apparentes différentes : alignées
 * sur une même ligne sous chaque question, elles donnaient une barre
 * irrégulière que rien ne justifiait. Une silhouette unique, tracée à
 * jointures rondes, les met au même gabarit — et l'arrondi n'est plus une
 * intention répétée dans dix tracés, c'est une propriété du rendu.
 *
 * `plein` porte l'état actif d'un marque-page : la forme dit l'état sans qu'on
 * lise.
 */
const ARRONDIES: Partial<Record<NomDIcone, { corps: string; details?: string; plein?: boolean }>> = {
  'marque-page': {
    corps: 'M6.5 3.6h11a1.4 1.4 0 0 1 1.4 1.4v15.4l-6.9-4.1-6.9 4.1V5a1.4 1.4 0 0 1 1.4-1.4z',
  },
  'marque-page-plein': {
    corps: 'M6.5 3.6h11a1.4 1.4 0 0 1 1.4 1.4v15.4l-6.9-4.1-6.9 4.1V5a1.4 1.4 0 0 1 1.4-1.4z',
    plein: true,
  },
  // Une ampoule : le geste « comprendre », pas « signaler ».
  explication: {
    corps: 'M12 3.2a6.4 6.4 0 0 0-3.6 11.7v1.9a1.2 1.2 0 0 0 1.2 1.2h4.8a1.2 1.2 0 0 0 1.2-1.2v-1.9A6.4 6.4 0 0 0 12 3.2z',
    details: 'M10 20.8h4',
  },
  // Un livre ouvert — la même silhouette que « Formations » dans la barre.
  lecon: {
    corps: 'M3.4 5.6A1.5 1.5 0 0 1 4.9 4.1h3.3A3.7 3.7 0 0 1 12 6.7a3.7 3.7 0 0 1 3.8-2.6h3.3a1.5 1.5 0 0 1 1.5 1.5v10.6a1.5 1.5 0 0 1-1.5 1.5h-3.3A3.7 3.7 0 0 0 12 20.5a3.7 3.7 0 0 0-3.8-2.7H4.9a1.5 1.5 0 0 1-1.5-1.5z',
    details: 'M12 6.7v13.8',
  },
  // Une flèche qui SORT d'une boîte — la grammaire universelle de l'envoi.
  partager: {
    corps: 'M4.2 13.4v5.4a1.4 1.4 0 0 0 1.4 1.4h12.8a1.4 1.4 0 0 0 1.4-1.4v-5.4',
    details: 'M12 3.4v11.4M8.2 7.2 12 3.4l3.8 3.8',
  },
  // Une bulle — le compte des réponses données.
  reponses: {
    corps: 'M4.6 4.4h14.8a1.4 1.4 0 0 1 1.4 1.4v8.8a1.4 1.4 0 0 1-1.4 1.4H9.8l-4.4 3.6v-3.6H4.6a1.4 1.4 0 0 1-1.4-1.4V5.8a1.4 1.4 0 0 1 1.4-1.4z',
  },
  // Une cible — le taux de réussite.
  cible: {
    corps: 'M12 3.4a8.6 8.6 0 1 1 0 17.2 8.6 8.6 0 0 1 0-17.2z',
    details: 'M12 8.2a3.8 3.8 0 1 1 0 7.6 3.8 3.8 0 0 1 0-7.6z',
  },
  oeil: {
    corps: 'M12 5.4c-4.8 0-8.6 4.3-8.6 6.6s3.8 6.6 8.6 6.6 8.6-4.3 8.6-6.6S16.8 5.4 12 5.4z',
    details: 'M12 9.1a2.9 2.9 0 1 1 0 5.8 2.9 2.9 0 0 1 0-5.8z',
  },
  drapeau: {
    corps: 'M5.4 3.4v17.2',
    details: 'M5.4 4.4h12.4l-2.4 4.2 2.4 4.2H5.4',
  },
  'trois-points': {
    corps: 'M12 5.2a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2zM12 10.4a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2zM12 15.6a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2z',
    plein: true,
  },
  // Un calendrier — la date du concours, §110. Deux anneaux et une ligne de
  // séparation : la silhouette se reconnaît à 16 px, ce qu'une grille de jours
  // ne fait pas.
  calendrier: {
    corps: 'M4.6 5.8h14.8a1.4 1.4 0 0 1 1.4 1.4v11.4a1.4 1.4 0 0 1-1.4 1.4H4.6a1.4 1.4 0 0 1-1.4-1.4V7.2a1.4 1.4 0 0 1 1.4-1.4z',
    details: 'M3.2 10.4h17.6M8 3.6v4.2M16 3.6v4.2',
  },
  // Deux maillons ouverts, en diagonale : la silhouette d'un lien se reconnaît
  // à 16 px, et elle ne se confond avec aucune des flèches de la barre.
  lien: {
    corps: 'M10.1 13.9a4.2 4.2 0 0 0 6.3.45l2.1-2.1a4.2 4.2 0 0 0-5.94-5.94l-1.2 1.2',
    details: 'M13.9 10.1a4.2 4.2 0 0 0-6.3-.45l-2.1 2.1a4.2 4.2 0 0 0 5.94 5.94l1.2-1.2',
  },
  /*
   * **La chaîne WhatsApp** — [DECISIONS §313].
   *
   * Une bulle avec sa queue en bas à gauche, et un combiné dedans : la
   * silhouette se reconnaît sans être le logo de la marque. C'est délibéré.
   * Reproduire le glyphe officiel — aplat, vert de marque, proportions exactes
   * — aurait fait entrer une identité étrangère au milieu de la nôtre, et
   * jurerait avec les vingt autres icônes, toutes en tracé sur la même grille.
   *
   * Le vert de WhatsApp n'apparaît nulle part non plus : l'icône prend
   * `currentColor`, comme les autres, et suit donc le thème sombre.
   */
  whatsapp: {
    corps: 'M20.4 11.8a8.4 8.4 0 0 1-12.5 7.35L3.6 20.4l1.28-4.25A8.4 8.4 0 1 1 20.4 11.8z',
    details: 'M9.4 8.9a1 1 0 0 1 1.5-.3l1 1a1 1 0 0 1 .1 1.3l-.4.5a5.5 5.5 0 0 0 2.1 2.1l.5-.4a1 1 0 0 1 1.3.1l1 1a1 1 0 0 1-.3 1.5c-1.4.8-3.4.1-5.2-1.6s-2.4-3.8-1.6-5.2z',
  },
  /*
   * **L'image d'un coupon** — [DECISIONS §330].
   *
   * Un cadre, une colline et un soleil : la silhouette universelle du fichier
   * image, et elle se reconnaît à 16 px. Ce n'est ni `partager` — qui dit
   * « envoyer à quelqu'un » — ni `lien`, qui dit « obtenir une adresse » : les
   * trois gestes cohabitent sur la même ligne d'un lot, et leur donner deux
   * fois le même signe aurait rendu la ligne illisible.
   */
  image: {
    corps: 'M4.6 4.4h14.8a1.4 1.4 0 0 1 1.4 1.4v12.4a1.4 1.4 0 0 1-1.4 1.4H4.6a1.4 1.4 0 0 1-1.4-1.4V5.8a1.4 1.4 0 0 1 1.4-1.4z',
    details: 'M3.4 16.4 8 11.8l3.4 3.4 2.8-2.8 5.4 5.4M9.4 9.2a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0z',
  },
  /*
   * **Le refus d'une action** — [DECISIONS §338].
   *
   * Le triangle et non une croix : la croix vit déjà à trois centimètres de là,
   * sur le bouton qui FERME le message. Deux croix côte à côte, l'une pour dire
   * « ça n'a pas marché » et l'autre pour dire « j'ai lu », se lisent comme un
   * seul bouton dessiné deux fois.
   *
   * Le point du bas est un segment de longueur nulle : `strokeLinecap="round"`
   * en fait un rond parfait, au même trait que le reste. Un `<circle>` aurait
   * demandé une troisième forme dans un enregistrement qui n'en accepte que
   * deux, et il aurait fallu le remplir — donc porter une épaisseur apparente
   * différente de celle du triangle.
   */
  alerte: {
    corps: 'M12 3.4 21.6 20.6H2.4z',
    details: 'M12 9.6v4.6M12 17.8h.01',
  },
}


export function Icone({
  nom,
  taille = 20,
  titre,
  className,
}: {
  nom: NomDIcone
  /** En pixels. Le rail utilise 26 ; dans le texte, 16 à 20 s'alignent mieux. */
  taille?: number
  /** Quand l'icône porte SEULE le sens : elle devient alors une image nommée. */
  titre?: string
  className?: string
}) {
  const arrondie = ARRONDIES[nom]

  if (arrondie !== undefined) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={taille}
        height={taille}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        role={titre === undefined ? undefined : 'img'}
        aria-hidden={titre === undefined ? true : undefined}
        aria-label={titre}
        style={{ flexShrink: 0 }}
      >
        <path d={arrondie.corps} fill={arrondie.plein === true ? 'currentColor' : 'none'} />
        {arrondie.details !== undefined && <path d={arrondie.details} />}
      </svg>
    )
  }

  return (
    <svg
      viewBox="0 0 24 24"
      width={taille}
      height={taille}
      fill="currentColor"
      className={className}
      role={titre === undefined ? undefined : 'img'}
      aria-hidden={titre === undefined ? true : undefined}
      aria-label={titre}
      // `shrink-0` : dans une ligne en flex, une icône ne doit jamais être
      // écrasée par un titre long — elle deviendrait un trait illisible.
      style={{ flexShrink: 0 }}
    >
      {TRACES[nom]}
    </svg>
  )
}
